import React, { useState, useEffect } from 'react';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';

const Ensemble = ({ projectId }) => {
  // States für künstlerisches Team und Schauspielensemble
  const [artisticTeam, setArtisticTeam] = useState([]);
  const [actorEnsemble, setActorEnsemble] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'ascending', type: null });
  
  // Neuer Eintrag für beide Teams (jetzt mit Adressfeldern)
  const [newEntry, setNewEntry] = useState({
    name: '',
    email: '',
    telefon: '',
    funktion: '',
    bemerkungen: '',
    strasse: '',
    hausnummer: '',
    plz: '',
    ort: '',
    land: '',
    type: 'artistisch',
  });

  // ---------------------------------------------
  // 1. Beim Laden der Komponente Daten aus dem LocalStorage holen
  // ---------------------------------------------
  useEffect(() => {
    const loadSavedData = () => {
      if (projectId) {
        console.log('Loading ensemble data for project:', projectId);
        
        // Laden des künstlerischen Teams
        const savedArtisticTeam = localStorage.getItem(`artisticTeam_${projectId}`);
        if (savedArtisticTeam) {
          try {
            const parsedArtisticTeam = JSON.parse(savedArtisticTeam);
            if (parsedArtisticTeam && parsedArtisticTeam.length > 0) {
              console.log('Loading artistic team:', parsedArtisticTeam);
              setArtisticTeam(parsedArtisticTeam);
            }
          } catch (error) {
            console.error('Error loading artistic team:', error);
          }
        }

        // Laden des Schauspielensembles
        const savedActorEnsemble = localStorage.getItem(`actorEnsemble_${projectId}`);
        if (savedActorEnsemble) {
          try {
            const parsedActorEnsemble = JSON.parse(savedActorEnsemble);
            if (parsedActorEnsemble && parsedActorEnsemble.length > 0) {
              console.log('Loading actor ensemble:', parsedActorEnsemble);
              setActorEnsemble(parsedActorEnsemble);
            }
          } catch (error) {
            console.error('Error loading actor ensemble:', error);
          }
        }
      }
    };

    loadSavedData();
  }, [projectId]);

  // ---------------------------------------------
  // 2. Speichern bei Änderungen in den LocalStorage
  // ---------------------------------------------
  useEffect(() => {
    const saveData = () => {
      if (projectId) {
        // Künstlerisches Team speichern
        localStorage.setItem(`artisticTeam_${projectId}`, JSON.stringify(artisticTeam));
        
        // Schauspielensemble speichern
        localStorage.setItem(`actorEnsemble_${projectId}`, JSON.stringify(actorEnsemble));
      }
    };

    // Speichert jedes Mal neu, wenn sich artisticTeam oder actorEnsemble ändern
    saveData();
  }, [artisticTeam, actorEnsemble, projectId]);

  // ---------------------------------------------
  // 3. Eingabeformular (oben) – neuen Eintrag hinzufügen
  // ---------------------------------------------
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewEntry((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const addEntry = () => {
    // Bedingung entfernt - erlaubt Hinzufügen ohne ausgefüllte Pflichtfelder
    const entryToAdd = { 
      ...newEntry, 
      id: Date.now() 
    };
    
    if (newEntry.type === 'artistisch') {
      setArtisticTeam((prev) => [...prev, entryToAdd]);
    } else {
      setActorEnsemble((prev) => [...prev, entryToAdd]);
    }
    
    // Formular zurücksetzen
    setNewEntry({
      name: '',
      email: '',
      telefon: '',
      funktion: '',
      bemerkungen: '',
      strasse: '',
      hausnummer: '',
      plz: '',
      ort: '',
      land: '',
      type: newEntry.type, // Typ beibehalten
    });
  };
  // ---------------------------------------------
  // 4. Löschen eines Eintrags
  // ---------------------------------------------
  const deleteEntry = (id, type) => {
    if (window.confirm('Möchten Sie diesen Eintrag wirklich löschen?')) {
      if (type === 'artistisch') {
        setArtisticTeam(prev => {
          const updatedTeam = prev.filter(entry => entry.id !== id);
          if (updatedTeam.length === 0) {
            localStorage.removeItem(`artisticTeam_${projectId}`);
          } else {
            localStorage.setItem(`artisticTeam_${projectId}`, JSON.stringify(updatedTeam));
          }
          return updatedTeam;
        });
      } else {
        setActorEnsemble(prev => {
          const updatedEnsemble = prev.filter(entry => entry.id !== id);
          if (updatedEnsemble.length === 0) {
            localStorage.removeItem(`actorEnsemble_${projectId}`);
          } else {
            localStorage.setItem(`actorEnsemble_${projectId}`, JSON.stringify(updatedEnsemble));
          }
          return updatedEnsemble;
        });
      }
    }
  };

  // ---------------------------------------------
  // 5. In-line Bearbeitung in den Tabellenzellen
  // ---------------------------------------------
  // Eine Funktion, die – abhängig vom Typ (artistisch vs. schauspieler) – 
  // den Wert im jeweiligen State aktualisiert
  const handleEnsembleChange = (type, id, field, newValue) => {
    if (type === 'artistisch') {
      setArtisticTeam((prev) =>
        prev.map((entry) =>
          entry.id === id ? { ...entry, [field]: newValue } : entry
        )
      );
    } else {
      setActorEnsemble((prev) =>
        prev.map((entry) =>
          entry.id === id ? { ...entry, [field]: newValue } : entry
        )
      );
    }
  };

  // ---------------------------------------------
  // 6. CSV/Excel Export
  // ---------------------------------------------
  const exportToCSV = (data, filename) => {
    const csv = Papa.unparse(data);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // ---------------------------------------------
  // 7. CSV/Excel Import
  // ---------------------------------------------
  const handleFileUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      let data;
      if (file.name.endsWith('.csv')) {
        // CSV Verarbeitung
        const text = await file.text();
        const results = Papa.parse(text, {
          header: true,
          skipEmptyLines: true,
        });
        data = results.data;
      } else {
        // Excel Verarbeitung
        const buffer = await file.arrayBuffer();
        const workbook = XLSX.read(buffer, {
          type: 'array',
          dateNOS: true,
          cellDates: true,
        });
        
        // Ersten Sheet auswählen
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        
        // Konvertierung zu JSON
        data = XLSX.utils.sheet_to_json(worksheet, {
          raw: false,
        });
      }

      const newEntries = data
        .filter((entry) => entry.name || entry.Name)
        .map((entry) => ({
          id: Date.now() + Math.random(),
          name: entry.name || entry.Name || '',
          email: entry.email || entry.Email || '',
          telefon: entry.telefon || entry.Telefon || '',
          funktion: entry.funktion || entry.Funktion || '',
          bemerkungen: entry.bemerkungen || entry.Bemerkungen || '',
          strasse: entry.strasse || entry.Strasse || '',
          hausnummer: entry.hausnummer || entry.Hausnummer || '',
          plz: entry.plz || entry.PLZ || '',
          ort: entry.ort || entry.Ort || '',
          land: entry.land || entry.Land || '',
          type,
        }));

      if (type === 'artistisch') {
        setArtisticTeam((prev) => [...prev, ...newEntries]);
      } else {
        setActorEnsemble((prev) => [...prev, ...newEntries]);
      }

      alert(`${newEntries.length} Einträge wurden erfolgreich importiert.`);
    } catch (error) {
      console.error('Fehler beim Datei-Import:', error);
      alert('Fehler beim Import der Datei. Bitte prüfen Sie das Format.');
    }

    // Input-Feld zurücksetzen
    e.target.value = '';
  };

  // ---------------------------------------------
  // 8. Sortierung der Tabelle
  // ---------------------------------------------
  const requestSort = (key, type) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.type === type && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction, type });
  };

  const getSortedData = (data, type) => {
    if (!sortConfig.key || sortConfig.type !== type) return data;
    
    const sortableItems = [...data];
    sortableItems.sort((a, b) => {
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === 'ascending' ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === 'ascending' ? 1 : -1;
      }
      return 0;
    });
    
    return sortableItems;
  };

  const getSortIcon = (key, type) => {
    if (sortConfig.key === key && sortConfig.type === type) {
      return sortConfig.direction === 'ascending' ? ' ↑' : ' ↓';
    }
    return '';
  };

  // Sortierte Daten erhalten
  const sortedArtisticTeam = getSortedData(artisticTeam, 'artistisch');
  const sortedActorEnsemble = getSortedData(actorEnsemble, 'schauspieler');

  // ---------------------------------------------
  // RENDER
  // ---------------------------------------------
  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-4">Ensemble</h2>
      
      {/* Eingabeformular */}
      <div className="mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
          <select
            name="type"
            value={newEntry.type}
            onChange={handleInputChange}
            className="border p-2"
          >
            <option value="artistisch">Künstlerisches Team</option>
            <option value="schauspieler">Schauspielensemble</option>
          </select>

          <input
            type="text"
            name="name"
            value={newEntry.name}
            onChange={handleInputChange}
            placeholder="Name"
            className="border p-2"
          />

          <input
            type="email"
            name="email"
            value={newEntry.email}
            onChange={handleInputChange}
            placeholder="E-Mail"
            className="border p-2"
          />

          <input
            type="tel"
            name="telefon"
            value={newEntry.telefon}
            onChange={handleInputChange}
            placeholder="Telefon"
            className="border p-2"
          />

          <input
            type="text"
            name="funktion"
            value={newEntry.funktion}
            onChange={handleInputChange}
            placeholder="Funktion"
            className="border p-2"
          />

          <input
            type="text"
            name="bemerkungen"
            value={newEntry.bemerkungen}
            onChange={handleInputChange}
            placeholder="Bemerkungen"
            className="border p-2"
          />

          {/* Neue Adressfelder */}
          <input
            type="text"
            name="strasse"
            value={newEntry.strasse}
            onChange={handleInputChange}
            placeholder="Straße"
            className="border p-2"
          />

          <input
            type="text"
            name="hausnummer"
            value={newEntry.hausnummer}
            onChange={handleInputChange}
            placeholder="Hausnummer"
            className="border p-2"
          />

          <input
            type="text"
            name="plz"
            value={newEntry.plz}
            onChange={handleInputChange}
            placeholder="PLZ"
            className="border p-2"
          />

          <input
            type="text"
            name="ort"
            value={newEntry.ort}
            onChange={handleInputChange}
            placeholder="Ort"
            className="border p-2"
          />

          <input
            type="text"
            name="land"
            value={newEntry.land}
            onChange={handleInputChange}
            placeholder="Land"
            className="border p-2"
          />

          <button
            onClick={addEntry}
            className="bg-blue-500 text-white p-2 rounded hover:bg-blue-600"
          >
            Eintrag hinzufügen
          </button>
        </div>
      </div>

      {/* Import/Export Buttons */}
      <div className="mb-4 flex flex-wrap gap-4">
        {/* Künstlerisches Team Import/Export */}
        <div>
          <input
            type="file"
            accept=".csv,.xlsx,.xls"
            onChange={(e) => handleFileUpload(e, 'artistisch')}
            className="hidden"
            id="csvUploadArtistic"
          />
          <label 
            htmlFor="csvUploadArtistic" 
            className="bg-green-500 text-white p-2 rounded cursor-pointer hover:bg-green-600"
          >
            Künstlerisches Team importieren
          </label>
          <button
            onClick={() => exportToCSV(artisticTeam, 'kuenstlerisches_team.csv')}
            className="bg-blue-500 text-white p-2 rounded ml-2 hover:bg-blue-600"
          >
            Künstlerisches Team exportieren
          </button>
        </div>

        {/* Schauspielensemble Import/Export */}
        <div>
          <input
            type="file"
            accept=".csv,.xlsx,.xls"
            onChange={(e) => handleFileUpload(e, 'schauspieler')}
            className="hidden"
            id="csvUploadActors"
          />
          <label 
            htmlFor="csvUploadActors" 
            className="bg-green-500 text-white p-2 rounded cursor-pointer hover:bg-green-600"
          >
            Schauspielensemble importieren
          </label>
          <button
            onClick={() => exportToCSV(actorEnsemble, 'schauspielerensemble.csv')}
            className="bg-blue-500 text-white p-2 rounded ml-2 hover:bg-blue-600"
          >
            Schauspielensemble exportieren
          </button>
        </div>
      </div>

      {/* Tabelle Künstlerisches Team */}
      <div className="mb-6">
        <h3 className="text-xl font-semibold mb-2">Künstlerisches Team</h3>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse border">
            <thead>
              <tr className="bg-gray-200">
                <th 
                  className="border p-2 cursor-pointer hover:bg-gray-300"
                  onClick={() => requestSort('name', 'artistisch')}
                >
                  Name{getSortIcon('name', 'artistisch')}
                </th>
                <th 
                  className="border p-2 cursor-pointer hover:bg-gray-300"
                  onClick={() => requestSort('email', 'artistisch')}
                >
                  E-Mail{getSortIcon('email', 'artistisch')}
                </th>
                <th 
                  className="border p-2 cursor-pointer hover:bg-gray-300"
                  onClick={() => requestSort('telefon', 'artistisch')}
                >
                  Telefon{getSortIcon('telefon', 'artistisch')}
                </th>
                <th 
                  className="border p-2 cursor-pointer hover:bg-gray-300"
                  onClick={() => requestSort('funktion', 'artistisch')}
                >
                  Funktion{getSortIcon('funktion', 'artistisch')}
                </th>
                <th 
                  className="border p-2 cursor-pointer hover:bg-gray-300"
                  onClick={() => requestSort('bemerkungen', 'artistisch')}
                >
                  Bemerkungen{getSortIcon('bemerkungen', 'artistisch')}
                </th>
                <th 
                  className="border p-2 cursor-pointer hover:bg-gray-300"
                  onClick={() => requestSort('strasse', 'artistisch')}
                >
                  Straße{getSortIcon('strasse', 'artistisch')}
                </th>
                <th 
                  className="border p-2 cursor-pointer hover:bg-gray-300"
                  onClick={() => requestSort('hausnummer', 'artistisch')}
                >
                  Hausnummer{getSortIcon('hausnummer', 'artistisch')}
                </th>
                <th 
                  className="border p-2 cursor-pointer hover:bg-gray-300"
                  onClick={() => requestSort('plz', 'artistisch')}
                >
                  PLZ{getSortIcon('plz', 'artistisch')}
                </th>
                <th 
                  className="border p-2 cursor-pointer hover:bg-gray-300"
                  onClick={() => requestSort('ort', 'artistisch')}
                >
                  Ort{getSortIcon('ort', 'artistisch')}
                </th>
                <th 
                  className="border p-2 cursor-pointer hover:bg-gray-300"
                  onClick={() => requestSort('land', 'artistisch')}
                >
                  Land{getSortIcon('land', 'artistisch')}
                </th>
                <th className="border p-2">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {sortedArtisticTeam.map((entry) => (
                <tr key={entry.id}>
                  {/* Statt festem Text verwenden wir <input>, um inline zu bearbeiten */}
                  <td className="border p-2">
                    <input
                      type="text"
                      value={entry.name || ''}
                      onChange={(e) =>
                        handleEnsembleChange('artistisch', entry.id, 'name', e.target.value)
                      }
                      className="w-full"
                    />
                  </td>
                  <td className="border p-2">
                    <input
                      type="email"
                      value={entry.email || ''}
                      onChange={(e) =>
                        handleEnsembleChange('artistisch', entry.id, 'email', e.target.value)
                      }
                      className="w-full"
                    />
                  </td>
                  <td className="border p-2">
                    <input
                      type="tel"
                      value={entry.telefon || ''}
                      onChange={(e) =>
                        handleEnsembleChange('artistisch', entry.id, 'telefon', e.target.value)
                      }
                      className="w-full"
                    />
                  </td>
                  <td className="border p-2">
                    <input
                      type="text"
                      value={entry.funktion || ''}
                      onChange={(e) =>
                        handleEnsembleChange('artistisch', entry.id, 'funktion', e.target.value)
                      }
                      className="w-full"
                    />
                  </td>
                  <td className="border p-2">
                    <input
                      type="text"
                      value={entry.bemerkungen || ''}
                      onChange={(e) =>
                        handleEnsembleChange('artistisch', entry.id, 'bemerkungen', e.target.value)
                      }
                      className="w-full"
                    />
                  </td>
                  {/* Neue Adressfelder */}
                  <td className="border p-2">
                    <input
                      type="text"
                      value={entry.strasse || ''}
                      onChange={(e) =>
                        handleEnsembleChange('artistisch', entry.id, 'strasse', e.target.value)
                      }
                      className="w-full"
                    />
                  </td>
                  <td className="border p-2">
                    <input
                      type="text"
                      value={entry.hausnummer || ''}
                      onChange={(e) =>
                        handleEnsembleChange('artistisch', entry.id, 'hausnummer', e.target.value)
                      }
                      className="w-full"
                    />
                  </td>
                  <td className="border p-2">
                    <input
                      type="text"
                      value={entry.plz || ''}
                      onChange={(e) =>
                        handleEnsembleChange('artistisch', entry.id, 'plz', e.target.value)
                      }
                      className="w-full"
                    />
                  </td>
                  <td className="border p-2">
                    <input
                      type="text"
                      value={entry.ort || ''}
                      onChange={(e) =>
                        handleEnsembleChange('artistisch', entry.id, 'ort', e.target.value)
                      }
                      className="w-full"
                    />
                  </td>
                  <td className="border p-2">
                    <input
                      type="text"
                      value={entry.land || ''}
                      onChange={(e) =>
                        handleEnsembleChange('artistisch', entry.id, 'land', e.target.value)
                      }
                      className="w-full"
                    />
                  </td>
                  <td className="border p-2">
                    <button
                      onClick={() => deleteEntry(entry.id, 'artistisch')}
                      className="bg-red-500 text-white p-1 rounded hover:bg-red-600"
                    >
                      Löschen
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Tabelle Schauspielensemble */}
      <div>
        <h3 className="text-xl font-semibold mb-2">Schauspielensemble</h3>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse border">
            <thead>
              <tr className="bg-gray-200">
                <th 
                  className="border p-2 cursor-pointer hover:bg-gray-300"
                  onClick={() => requestSort('name', 'schauspieler')}
                >
                  Name{getSortIcon('name', 'schauspieler')}
                </th>
                <th 
                  className="border p-2 cursor-pointer hover:bg-gray-300"
                  onClick={() => requestSort('email', 'schauspieler')}
                >
                  E-Mail{getSortIcon('email', 'schauspieler')}
                </th>
                <th 
                  className="border p-2 cursor-pointer hover:bg-gray-300"
                  onClick={() => requestSort('telefon', 'schauspieler')}
                >
                  Telefon{getSortIcon('telefon', 'schauspieler')}
                </th>
                <th 
                  className="border p-2 cursor-pointer hover:bg-gray-300"
                  onClick={() => requestSort('funktion', 'schauspieler')}
                >
                  Funktion{getSortIcon('funktion', 'schauspieler')}
                </th>
                <th 
                  className="border p-2 cursor-pointer hover:bg-gray-300"
                  onClick={() => requestSort('bemerkungen', 'schauspieler')}
                >
                  Bemerkungen{getSortIcon('bemerkungen', 'schauspieler')}
                </th>
                <th 
                  className="border p-2 cursor-pointer hover:bg-gray-300"
                  onClick={() => requestSort('strasse', 'schauspieler')}
                >
                  Straße{getSortIcon('strasse', 'schauspieler')}
                </th>
                <th 
                  className="border p-2 cursor-pointer hover:bg-gray-300"
                  onClick={() => requestSort('hausnummer', 'schauspieler')}
                >
                  Hausnummer{getSortIcon('hausnummer', 'schauspieler')}
                </th>
                <th 
                  className="border p-2 cursor-pointer hover:bg-gray-300"
                  onClick={() => requestSort('plz', 'schauspieler')}
                >
                  PLZ{getSortIcon('plz', 'schauspieler')}
                </th>
                <th 
                  className="border p-2 cursor-pointer hover:bg-gray-300"
                  onClick={() => requestSort('ort', 'schauspieler')}
                >
                  Ort{getSortIcon('ort', 'schauspieler')}
                </th>
                <th 
                  className="border p-2 cursor-pointer hover:bg-gray-300"
                  onClick={() => requestSort('land', 'schauspieler')}
                >
                  Land{getSortIcon('land', 'schauspieler')}
                </th>
                <th className="border p-2">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {sortedActorEnsemble.map((entry) => (
                <tr key={entry.id}>
                  <td className="border p-2">
                    <input
                      type="text"
                      value={entry.name || ''}
                      onChange={(e) =>
                        handleEnsembleChange('schauspieler', entry.id, 'name', e.target.value)
                      }
                      className="w-full"
                    />
                  </td>
                  <td className="border p-2">
                    <input
                      type="email"
                      value={entry.email || ''}
                      onChange={(e) =>
                        handleEnsembleChange('schauspieler', entry.id, 'email', e.target.value)
                      }
                      className="w-full"
                    />
                  </td>
                  <td className="border p-2">
                    <input
                      type="tel"
                      value={entry.telefon || ''}
                      onChange={(e) =>
                        handleEnsembleChange('schauspieler', entry.id, 'telefon', e.target.value)
                      }
                      className="w-full"
                    />
                  </td>
                  <td className="border p-2">
                    <input
                      type="text"
                      value={entry.funktion || ''}
                      onChange={(e) =>
                        handleEnsembleChange('schauspieler', entry.id, 'funktion', e.target.value)
                      }
                      className="w-full"
                    />
                  </td>
                  <td className="border p-2">
                    <input
                      type="text"
                      value={entry.bemerkungen || ''}
                      onChange={(e) =>
                        handleEnsembleChange('schauspieler', entry.id, 'bemerkungen', e.target.value)
                      }
                      className="w-full"
                    />
                  </td>
                  {/* Neue Adressfelder */}
                  <td className="border p-2">
                    <input
                      type="text"
                      value={entry.strasse || ''}
                      onChange={(e) =>
                        handleEnsembleChange('schauspieler', entry.id, 'strasse', e.target.value)
                      }
                      className="w-full"
                    />
                  </td>
                  <td className="border p-2">
                    <input
                      type="text"
                      value={entry.hausnummer || ''}
                      onChange={(e) =>
                        handleEnsembleChange('schauspieler', entry.id, 'hausnummer', e.target.value)
                      }
                      className="w-full"
                    />
                  </td>
                  <td className="border p-2">
                    <input
                      type="text"
                      value={entry.plz || ''}
                      onChange={(e) =>
                        handleEnsembleChange('schauspieler', entry.id, 'plz', e.target.value)
                      }
                      className="w-full"
                    />
                  </td>
                  <td className="border p-2">
                    <input
                      type="text"
                      value={entry.ort || ''}
                      onChange={(e) =>
                        handleEnsembleChange('schauspieler', entry.id, 'ort', e.target.value)
                      }
                      className="w-full"
                    />
                  </td>
                  <td className="border p-2">
                    <input
                      type="text"
                      value={entry.land || ''}
                      onChange={(e) =>
                        handleEnsembleChange('schauspieler', entry.id, 'land', e.target.value)
                      }
                      className="w-full"
                    />
                  </td>
                  <td className="border p-2">
                    <button
                      onClick={() => deleteEntry(entry.id, 'schauspieler')}
                      className="bg-red-500 text-white p-1 rounded hover:bg-red-600"
                    >
                      Löschen
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Ensemble;